import React from 'react';
import axios, { CancelTokenSource } from 'axios';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';
import download from 'downloadjs';
import dayjs from 'dayjs';
import Dropzone from 'react-dropzone';

import Aside from '../../components/shared/aside/Aside';
import Icon from '../../components/shared/icon/Icon';
import Spinner, { SpinnerSize } from '../../components/shared/spinner/Spinner';
import Tab from '../../components/shared/tab/Tab';
import TabItem from '../../components/shared/tab/TabItem';
import Button from '../../components/forms/button/Button';
import Modal from '../../components/shared/modal/Modal';
import ConfirmationModal from '../../components/shared/modal/ConfirmationModal';
import FormTextarea from '../../components/forms/formTextarea/FormTextarea';
import Carousel from '../../components/shared/carousel/Carousel';
import Contact from '../../components/shared/contact/Contact';
import AttachmentLink from '../../components/shared/attachmentlink/AttachmentLink';
import MultilineText from '../../components/shared/multilinetext/MultilineText';
import Arrow from '../../components/shared/arrow/Arrow';
import Tags from '../../components/shared/tags/Tags';
import AttachmentIcon from '../../components/shared/attachmentlink/AttachmentIcon';
import LinkButton from '../../components/forms/linkButton/LinkButton';
import DocumentFavorite from '../../components/shared/attachmentlink/DocumentFavorite';

import { IApplicationState } from '../../store/applicationState';
import { fetchGroupMeeting, fetchGroupMembers, fetchGroup, removeMeetingDocument, signupPerson, cancelPerson } from '../../store/group/groupActions';
import * as meetingService from '../../services/meeting';
import { IDocument, IGroup, IGroupMember, IMeeting } from '../../store/group/groupState';
import Member from '../group/Member';

import * as helpers from '../../genericExtensions';

interface IOwnAttachmentsState extends meetingService.IAttachmentsState {
    isDirty: boolean;
}

export interface IRouterParams {
    groupId: string;
    meetingId: string;
}

interface IState {
    cancelToken: CancelTokenSource;
    hasLoadedMessage: boolean;
    downloading: {
        'ACCEPTED': boolean;
        'TENTATIVE': boolean;
        'NEEDS-ACTION': boolean;
        'DECLINED': boolean;
        'ALLE': boolean;
    };
    showReminderModal: boolean;
    sendingReminder: boolean;
    reminderDescription: string;
    reminderFailure: any;
    showModal: { [id: string]: boolean };
    showSpinner: { [id: string]: boolean };
    isAttachments: boolean;
    attachmentsForm: IOwnAttachmentsState;
    isSavingAttachments: boolean;
}

interface IStateProps {
    groupId?: string;
    group?: IGroup;
    meeting?: IMeeting;
    groupMembers?: IGroupMember[];
    viewerIsDi: boolean;
}

interface IDispatchProps {
    fetchGroupMeeting: typeof fetchGroupMeeting;
    fetchGroupMembers: typeof fetchGroupMembers;
    fetchGroup: typeof fetchGroup;
    removeMeetingDocument: typeof removeMeetingDocument;
    signupPerson: typeof signupPerson;
    cancelPerson: typeof cancelPerson;
}

type Props = IStateProps & IDispatchProps & RouteComponentProps<IRouterParams>;

const formatSize = (bytes: number) => bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${Math.round(10 * bytes / (1024 * 1024)) / 10} MB`;
const formatDateTime = (dateString: string) => dayjs(dateString).format('DD.MM.YYYY HH:mm');

const formatStartDate = (dateString: string) => {
    const format = dayjs(dateString).format('dddd [d]. DD.MM.YYYY');

    return format.charAt(0).toUpperCase() + format.slice(1);
};

const formatDateRange = (startDate: string, endDate: string) => {
    const endTime = formatStartDate(startDate) === formatStartDate(endDate) ? dayjs(endDate).format('HH:mm') : formatDateTime(endDate);

    return `${dayjs(startDate).format('HH:mm')}-${endTime}`;
};

const slugify = (str: string) => str.trim()
    .replace(/[^a-z0-9æøå -]/gi, '-') // replace invalid chars with '-'
    .replace(/\s+/g, '-') // collapse whitespace and replace with '-'
    .replace(/-+/g, '-') // collapse dashes
    .replace(/^-+/, '') // trim '-' from start of text
    .replace(/-+$/, '');

const informationCard = (headline: string, icon: string, body: React.ReactNode) => <div className="information-card ">
    <div className="information-card__icon">
        <Icon name={icon} />
    </div>
    <div className="information-card__content">
        <div className="information-card__headline">{headline}</div>
        {body}
    </div>
</div>;

class MeetingIndex extends React.Component<Props, IState> {
    constructor(props: Props) {
        super(props);

        this.state = {
            cancelToken: axios.CancelToken.source(),
            hasLoadedMessage: false,
            downloading: {
                'ACCEPTED': false,
                'TENTATIVE': false,
                'NEEDS-ACTION': false,
                'DECLINED': false,
                'ALLE': false
            },
            showReminderModal: false,
            sendingReminder: false,
            reminderDescription: 'Vi har endnu ikke registreret et svar fra dig, og vi sender derfor invitationen igen.',
            reminderFailure: '',
            showModal: {},
            showSpinner: {},
            isAttachments: false,
            attachmentsForm: {
                ...meetingService.initialAttachmentsState,
                isDirty: false
            },
            isSavingAttachments: false
        }
    }

    public async componentDidMount() {
        if (!this.props.groupId) {
            this.props.fetchGroup(this.props.match.params.groupId, this.state.cancelToken);
        } else {
            this.props.fetchGroupMembers(this.props.groupId, this.state.cancelToken);
            await this.props.fetchGroupMeeting(this.props.groupId, this.props.match.params.meetingId, this.state.cancelToken);

            this.setState({
                hasLoadedMessage: true
            });
        }
    }

    public async componentDidUpdate(prevProps: Props) {
        if (!prevProps.groupId && this.props.groupId) {
            this.props.fetchGroup(this.props.groupId, this.state.cancelToken);
            this.props.fetchGroupMembers(this.props.groupId, this.state.cancelToken);
            await this.props.fetchGroupMeeting(this.props.groupId, this.props.match.params.meetingId, this.state.cancelToken);

            this.setState({
                hasLoadedMessage: true
            });
        }
    }

    public componentWillUnmount() {
        this.state.cancelToken.cancel();
    }

    public render() {
        if (!this.props.meeting) {
            return <Spinner size={SpinnerSize.Large} />;
        }

        return <React.Fragment>
            <section className="section">
                <div className="deck">
                    <div className="w--md">
                        <div className="flex-grid">
                            <div className={`flex-grid__col flex-grid__col--12 flex-grid__col--md-${this.props.meeting.documents.length || this.props.viewerIsDi ? 7 : 12}`}>
                                <article className="article article--no-initial">
                                    <div className="rich-text">
                                        <h1 className={`article__headline headline-lg`.cAppend(this.props.meeting.status === 'Cancelled', 't--strike')}>{this.props.meeting.subject}</h1>
                                        <div className="flex-grid bordered bordered--staticpadding information-grid">
                                            <div className={`flex-grid__col flex-grid__col--12 flex-grid__col--md-${this.props.meeting.location ? 6 : 12}`}>
                                                {informationCard(formatStartDate(this.props.meeting.startTime), 'calendar-simple', <div className="information-card__text">Kl. {formatDateRange(this.props.meeting.startTime, this.props.meeting.endTime)}</div>)}
                                            </div>
                                            {this.props.meeting.location ? <div className="flex-grid__col flex-grid__col--12 flex-grid__col--md-6">
                                                {informationCard(this.props.meeting.location, "pin", <div className="information-card__link"><a href={`https://maps.google.com/maps?hl=da&q=${encodeURIComponent(this.props.meeting.location)}`} target="_blank" rel="noopener noreferrer">Vis på kort</a></div>)}
                                            </div> : null}
                                        </div>
                                        <p>
                                            {!this.state.hasLoadedMessage && !this.props.meeting.htmlBody &&
                                                <Spinner />
                                            }
                                            {((this.state.hasLoadedMessage && this.props.meeting.htmlBody) || this.props.meeting.htmlBody) &&
                                                <div dangerouslySetInnerHTML={{ __html: this.props.meeting.htmlBody }} />
                                            }
                                            {this.state.hasLoadedMessage && !this.props.meeting.htmlBody && this.props.meeting.description && this.props.meeting.description
                                                .split('\n')
                                                .map((item, key) => <React.Fragment key={key}>{item}{'\n'}<br /></React.Fragment>)
                                            }
                                        </p>

                                    </div>
                                </article>
                            </div>
                            {this.props.meeting.documents.length || this.props.viewerIsDi ?
                                <div className="flex-grid__col flex-grid__col--12 flex-grid__col--md-4">
                                    <Aside headline="Materiale">
                                        <ul className="aside__list">
                                            {this.props.meeting.documents.map(document =>
                                                <li key={document.id} className="abstract-list__item aside__item article-abstract--significant abstract-list__item--no-image">
                                                    <div className="abstract-list__item__content">
                                                        <p className="meta">{formatSize(document.size)}</p>
                                                        <AttachmentLink className="button" document={document}>
                                                            <h4 className="abstract-list__item__content__head">
                                                                <DocumentFavorite id={document.id} groupId={this.props.groupId!} meetingId={this.props.meeting!.meetingId} isFavorite={document.isFavorite} />
                                                                <AttachmentIcon mimeType={document.mimeType} />
                                                                {document.fileName}
                                                            </h4>
                                                        </AttachmentLink>
                                                        {this.props.viewerIsDi &&
                                                            <React.Fragment>
                                                                <a href="/#" className="button button--action has-icon pointer" onClick={this.toggleModal(document.id)}>
                                                                    <span className="text">
                                                                        <span className="icon icon--right-spacing"><Icon name="trash" /></span>
                                                                        Slet dokument
                                                                    </span>
                                                                </a>
                                                                <ConfirmationModal show={this.state.showModal[document.id]} onAccept={this.removeDocument(document)} buttonsClickable={true} onReject={this.toggleModal(document.id)} acceptText="Slet">
                                                                    <h3>Slet dokument fra møde</h3>
                                                                    <p>Ønsker du at fjerne "{document.fileName}"-dokumentet fra mødet?</p>
                                                                </ConfirmationModal>
                                                            </React.Fragment>
                                                        }
                                                    </div>

                                                    <AttachmentLink className="button arrow__wrapper" document={document}>
                                                        <Icon name="download" classModifier="arrow" />
                                                    </AttachmentLink>
                                                </li>)
                                            }
                                            {this.props.viewerIsDi &&
                                                <React.Fragment>
                                                    <li className="abstract-list__item aside__item article-abstract--significant abstract-list__item--no-image">
                                                        <a href="/#" className="button button--action has-icon" onClick={this.toggleCreateAttachments}>
                                                            <span className="text">
                                                                <span className="icon icon--right-spacing">
                                                                    <Icon name="plus" />
                                                                </span>
                                                                Upload materiale
                                                            </span>
                                                        </a>
                                                    </li>
                                                    <Modal show={this.state.isAttachments} onClose={this.toggleCreateAttachments}>
                                                        <div className="rich-text">
                                                            <h3>Upload materiale</h3>
                                                        </div>

                                                        <div className="modal__content__form form">
                                                            <div className="form__section">
                                                                <div className="form__section__inner">
                                                                    <div className={'form__field form__field--files is-required'.cAppend(this.state.attachmentsForm.isDirty && this.validateAttachments(this.state.attachmentsForm.attachments), 'is-invalid')}>
                                                                        <label className="form__label">
                                                                            <span className="form__label__text">Dokumenter&nbsp;&nbsp;<strong>&#10033;</strong></span>
                                                                        </label>
                                                                        <div className="form__files__container">
                                                                            <Dropzone className="form__files__dragndrop" multiple={true} preventDropOnDocument={true} onDrop={this.onDropAttachment}>
                                                                                <p className="bodytext"><Arrow /> <span className="form__files__dragndrop__highlighted">Gennemse</span> eller slip din fil her.</p>
                                                                            </Dropzone>
                                                                            {this.state.attachmentsForm.attachments.length > 0 &&
                                                                                <div className="form__files__list">
                                                                                    <Tags alwaysVisible={true} useOverflow={true}>
                                                                                        {this.state.attachmentsForm.attachments.map((attachment, index) => {
                                                                                            return <li key={`file${index}`} className="tags__list__item">
                                                                                                <a href="/#" className="button button--tag" onClick={this.removeAttachment(index)}>
                                                                                                    <Icon name="trash" />
                                                                                                    <AttachmentIcon mimeType={attachment.type} />
                                                                                                    {attachment.name}
                                                                                                </a>
                                                                                            </li>;
                                                                                        })}
                                                                                    </Tags>
                                                                                </div>
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="modal__content__form__actions modal__content__form__actions--border">
                                                            <div className="flex-grid">
                                                                <div className="flex-grid__col flex-grid__col--12 flex-grid__col--sm-12 flex-grid__col--lg-12 align-right form__section__inner">
                                                                    <LinkButton modifierClass="button--discreet button--light" disabled={this.state.isSavingAttachments} onClick={this.toggleCreateAttachments}>Annuller</LinkButton>
                                                                    <LinkButton modifierClass="button--primary button--primary button--large" spinning={this.state.isSavingAttachments} onClick={this.saveAttachments}>Gem</LinkButton>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Modal>
                                                </React.Fragment>
                                            }
                                        </ul>
                                    </Aside>
                                </div>
                                :
                                null
                            }
                        </div>
                    </div>
                </div>
            </section>
            {this.props.meeting.participantStatuses.filter(s => s.member.role !== 'Responsible' && s.member.role !== 'Coordinator').length > 0 &&
                <section className="section">
                    <div className="deck">
                        <div className="w--md">
                            <Tab>
                                {this.renderParticipantTab('ACCEPTED')}
                                {this.renderParticipantTab('TENTATIVE')}
                                {this.renderParticipantTab('NEEDS-ACTION')}
                                {(this.props.viewerIsDi || (this.props.group && this.props.group.showMembersInfo !== 'none')) && this.renderParticipantTab('DECLINED')}
                            </Tab>
                        </div>
                    </div>
                </section>
            }

            <Carousel>
                {this.renderCarouselContact('Responsible')}
                {this.renderCarouselContact('Coordinator')}
            </Carousel>
        </React.Fragment>;
    }

    private renderParticipantTab(status: 'ACCEPTED' | 'TENTATIVE' | 'NEEDS-ACTION' | 'DECLINED') {
        if (!this.props.meeting) {
            return null;
        }

        const participants = this.props.meeting.participantStatuses.filter(s => s.status === status && s.member.role !== 'Responsible' && s.member.role !== 'Coordinator').sort((a, b) => (a.member.givenName > b.member.givenName) ? 1 : -1);

        if (!participants || !participants.length) {
            return null;
        }

        const cancelMessage = dayjs(this.props.meeting.startTime).isAfter(dayjs())
            ? "Der sendes en besked til personens kalender for at fjerne mødet."
            : "Der sendes ikke nogen besked til deltageren, da mødet er overstået.";

        return <TabItem header={`${helpers.participantStatusMap[status]} (${participants.length})`}>
            <div className="flex-grid no-spread align-left">
                {participants.map((participant, index) => <div className="flex-grid__col flex-grid__col--12 flex-grid__col--xs-4 flex-grid__col--sm-4 flex-grid__col--md-4" key={index}>
                    <Member member={participant.member} />
                    {this.props.viewerIsDi && participant.reply &&
                        <React.Fragment>
                            <a href="/#" className="button button--action has-icon pointer contact contact--minor" onClick={this.toggleModal(`${participant.member.alias}-reply`)}>
                                <span className="text">
                                    <span className="icon icon--right-spacing"><Icon name="emner" /></span>
                                    Se kommentar
                                </span>
                            </a>

                            <Modal show={this.state.showModal[`${participant.member.alias}-reply`]} onClose={this.toggleModal(`${participant.member.alias}-reply`)}>
                                <div className="rich-text">
                                    <h3>Kommentar til mødeindkaldelse fra {participant.member.fullName}</h3>
                                    <p><MultilineText text={participant.reply} /></p>
                                </div>
                                <div className="modal__content__form__actions action-buttons a--right">
                                    <Button modifierClass="button--primary button--small" onClick={this.toggleModal(`${participant.member.alias}-reply`)}>Luk</Button>
                                </div>
                            </Modal>
                        </React.Fragment>
                    }

                    {this.props.viewerIsDi && status !== 'ACCEPTED' &&
                        <React.Fragment>
                            <a href="/#" className="button button--action has-icon pointer contact contact--minor" onClick={this.toggleModal(participant.member.alias)}>
                                <span className="text">
                                    <span className="icon icon--right-spacing"><Icon name="share-email" /></span>
                                    Gensend invitation
                                </span>
                            </a>

                            <a href="/#" className="button button--action has-icon pointer contact contact--minor" onClick={this.signupPerson(participant.member)}>
                                <span className="text">
                                    <span className="icon icon--right-spacing">
                                        {this.state.showSpinner[participant.member.alias]
                                            ? <Spinner size={SpinnerSize.Small} />
                                            : <Icon name="checkmark" />
                                        }
                                    </span>
                                    Tilmeld person
                                </span>
                            </a>
                            <ConfirmationModal show={this.state.showModal[participant.member.alias]} onAccept={this.resendMeetingInvitation(participant.member)} buttonsClickable={!this.state.showSpinner[`modalBlocker${participant.member.alias}`]} onReject={this.toggleModal(participant.member.alias)} acceptText="Send">
                                <h3>Gensend invitation</h3>
                                <p>Vil du gensende seneste mødeinvitation til {participant.member.fullName}?</p>
                            </ConfirmationModal>
                        </React.Fragment>
                    }

                    {this.props.viewerIsDi && status !== 'DECLINED' &&
                        <React.Fragment>
                            <a href="/#" className="button button--action has-icon pointer contact contact--minor" onClick={this.toggleModal(`${participant.member.alias}-cancel`)}>
                                <span className="text">
                                    <span className="icon icon--right-spacing"><Icon name="close" /></span>
                                    Frameld person
                                </span>
                            </a>

                            <ConfirmationModal show={this.state.showModal[`${participant.member.alias}-cancel`]} onAccept={this.cancelPerson(participant.member)} buttonsClickable={!this.state.showSpinner[`modalBlocker${participant.member.alias}-cancel`]} onReject={this.toggleModal(`${participant.member.alias}-cancel`)} acceptText="Frameld">
                                <h3>Frameld deltager</h3>
                                <p>
                                    Vil du framelde {participant.member.fullName} fra mødet?<br />
                                    {cancelMessage}
                                </p>
                            </ConfirmationModal>
                        </React.Fragment>
                    }
                </div>)}
            </div>
            {this.renderDiButtons(status)}
        </TabItem>;
    }

    private renderDiButtons = (status: 'ACCEPTED' | 'TENTATIVE' | 'NEEDS-ACTION' | 'DECLINED' | 'ALLE') => {
        if (!this.props.viewerIsDi) {
            return null;
        }

        return <div className="action-buttons">
            {status === 'NEEDS-ACTION' && <React.Fragment>
                <Button modifierClass="button--small button--primary" onClick={this.toggleReminderModal}>Send påmindelse</Button>
                <Modal onClose={this.toggleReminderModal} show={this.state.showReminderModal}>
                    <form onSubmit={this.sendReminder}>
                        <div className="rich-text">
                            <h3>Send påmindelse</h3>
                            <p>Seneste mødeinvitation sendes til alle personer, som endnu ikke har svaret på invitationen. Hvis du ønsker, kan du indsætte en ekstra tekst i toppen af invitationen:</p>
                            <FormTextarea modifierClass="form__field--smalltextarea" name="description" label="Påmindelses besked" value={this.state.reminderDescription} onChange={this.setReminderText} notice={this.state.reminderFailure} invalid={!!this.state.reminderFailure} />
                        </div>

                        <div className="modal__content__form__actions action-buttons a--right">
                            <Button modifierClass="button--small" onClick={this.toggleReminderModal} type="button">Annuller</Button>
                            <Button modifierClass="button--primary button--small" type="submit" disabled={this.state.sendingReminder} spinning={this.state.sendingReminder}>Send</Button>
                        </div>
                    </form>
                </Modal>
            </React.Fragment>}
            <Button modifierClass="button--small button--primary" disabled={this.state.downloading[status] as boolean} onClick={this.downloadCsv(status)} spinning={this.state.downloading[status]}>Eksport til Excel ({helpers.participantStatusMap[status]})</Button>
            <Button modifierClass="button--small button--primary" disabled={this.state.downloading['ALLE'] as boolean} onClick={this.downloadCsv('ALLE')} spinning={this.state.downloading['ALLE']}>Eksport til Excel (Alle)</Button>
        </div>;
    }

    private renderCarouselContact(role: string) {
        if (!this.props.meeting || !this.props.groupMembers) {
            return null;
        }

        const responsibles = this.props.groupMembers.filter(s => s.role === role);

        return responsibles.map((responsible, index) => <Contact member={responsible} key={'role'.concat(index.toString())} useRoleTitle={true} />)
    }

    private toggleReminderModal = () => {
        this.setState({ showReminderModal: !this.state.showReminderModal });
    }

    private sendReminder = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        this.setState({ sendingReminder: true, reminderFailure: '' });

        try {
            await meetingService.sendReminder(this.props.groupId!, this.props.match.params.meetingId, this.state.reminderDescription, this.state.cancelToken);

            this.setState({ showReminderModal: false });
        } catch (error) {
            this.setState({ reminderFailure: error.message });
        }

        this.setState({ sendingReminder: false });
    }

    private setReminderText = (event: React.FormEvent<HTMLTextAreaElement>) => {
        this.setState({ reminderDescription: (event.target as HTMLTextAreaElement).value });
    };

    private downloadCsv = (status: string) => async () => {
        if (!this.props.meeting) {
            return;
        }

        this.setState({
            downloading: {
                ...this.state.downloading,
                [status]: true
            }
        });

        const csv = await meetingService.downloadParticipantStatuses(this.props.groupId!, this.props.match.params.meetingId, status, this.state.cancelToken);

        download(csv,
            `deltagere-${slugify(helpers.participantStatusMap[status])}-${this.props.match.params.groupId}-${slugify(this.props.meeting.subject)}.csv`,
            'text/csv');

        this.setState({
            downloading: {
                ...this.state.downloading,
                [status]: false
            }
        });
    }

    private removeDocument = (document: IDocument) => () => {
        this.props.removeMeetingDocument(this.props.groupId!, this.props.meeting!.meetingId, document.id, this.state.cancelToken);
        this.toggleModal(document.id)(); // Should really only do this on the GroupActionTypes.REMOVE_MEETING_DOCUMENT_COMPLETED but we can probably assume a typical error here is fine - namely, that someone else removed the document already and the end result is the same
    }

    private toggleModal = (modalId: string) => (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent | void) => {
        if (e) {
            e.preventDefault();
        }

        this.setState({ showModal: { ...this.state.showModal, [modalId]: !this.state.showModal[modalId] } });
    }

    private resendMeetingInvitation = (groupMember: IGroupMember) => async () => {
        this.setState({ showSpinner: { ...this.state.showSpinner, [`modalBlocker${groupMember.alias}`]: true } });
        await meetingService.resendInvitation(this.props.groupId!, this.props.meeting!.meetingId, groupMember.diNumber, this.state.cancelToken);
        this.setState({ showSpinner: { ...this.state.showSpinner, [`modalBlocker${groupMember.alias}`]: false } });
        this.toggleModal(groupMember.alias)();
    }

    private cancelPerson = (groupMember: IGroupMember) => async () => {
        this.setState({ showSpinner: { ...this.state.showSpinner, [`modalBlocker${groupMember.alias}-cancel`]: true } });
        await this.props.cancelPerson(this.props.groupId!, this.props.meeting!.meetingId, groupMember.diNumber, this.state.cancelToken);
        this.setState({ showSpinner: { ...this.state.showSpinner, [`modalBlocker${groupMember.alias}-cancel`]: false } });
        this.toggleModal(`${groupMember.alias}-cancel`)();
    }

    private signupPerson = (groupMember: IGroupMember) => async (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
        e.preventDefault();

        this.setState({ showSpinner: { ...this.state.showSpinner, [groupMember.alias]: !this.state.showSpinner[groupMember.alias] } });
        await this.props.signupPerson(this.props.groupId!, this.props.meeting!.meetingId, groupMember.diNumber, this.state.cancelToken);
        this.setState({ showSpinner: { ...this.state.showSpinner, [groupMember.alias]: !this.state.showSpinner[groupMember.alias] } });
    }

    private toggleCreateAttachments = (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        e.preventDefault();

        if (!this.state.isSavingAttachments) {
            this.setState(prevState => ({
                isAttachments: !prevState.isAttachments,
                attachmentsForm: {
                    ...this.state.attachmentsForm,
                    isDirty: false
                }
            }));
        }
    }

    private onDropAttachment = async (acceptedFiles: File[], rejectedFiles: File[]) => {
        if (acceptedFiles.length === 0 || rejectedFiles.length > 0) {
            return;
        }

        this.setState({
            attachmentsForm: {
                ...this.state.attachmentsForm,
                attachments: [...this.state.attachmentsForm.attachments, ...acceptedFiles]
            }
        });
    }

    private removeAttachment = (index: number) => (e: React.FormEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        this.setState({
            attachmentsForm: {
                ...this.state.attachmentsForm,
                attachments: this.state.attachmentsForm.attachments.filter((_, i) => i !== index)
            }
        });
    }

    private validateAttachments = (attachments: File[]) => {
        return !attachments || attachments.length === 0;
    }

    private validateAttachmentForm = () => {
        return !this.validateAttachments(this.state.attachmentsForm.attachments);
    }

    private saveAttachments = async (e: React.FormEvent<HTMLAnchorElement> | React.MouseEvent<HTMLElement>) => {
        e.preventDefault();

        if (!this.state.isSavingAttachments) {
            this.setState({
                attachmentsForm: {
                    ...this.state.attachmentsForm,
                    isDirty: true
                }
            });

            if (this.validateAttachmentForm()) {
                this.setState({
                    isSavingAttachments: true
                });

                await meetingService.addAttachments(this.props.groupId!, this.props.match.params.meetingId, this.state.attachmentsForm, this.state.cancelToken)
                await this.props.fetchGroupMeeting(this.props.groupId!, this.props.match.params.meetingId, this.state.cancelToken);

                this.setState({
                    isAttachments: false,
                    isSavingAttachments: false,
                    attachmentsForm: {
                        ...meetingService.initialAttachmentsState,
                        isDirty: false
                    }
                });
            }
        }
    }
}

const mapStateToProps = (state: IApplicationState, ownProps: RouteComponentProps<IRouterParams>): IStateProps => {
    const groupIds = state.groupData.groups.groups.allIds.filter(id =>
        state.groupData.groups.groups.byId[id].alias === ownProps.match.params.groupId ||
        state.groupData.groups.groups.byId[id].id === ownProps.match.params.groupId);
    const viewerIsDi = state.oidc.user && state.oidc.user.profile && state.oidc.user.profile.role === 'DI';

    if (!groupIds.length) { return { viewerIsDi }; }

    const groupId = groupIds[0];
    const knownGroupMeetings = state.groupData.meetings.byId[groupId];
    const members = state.groupData.members.byId[groupId];
    const group = state.groupData.groups.groups.byId[groupId];

    if (!members) {
        return { groupId, viewerIsDi, group };
    }

    const groupMembers = helpers.all(members.members);

    if (!knownGroupMeetings) { return { groupId, groupMembers, viewerIsDi, group }; }

    const groupMeetingResult = knownGroupMeetings.filter(m => m.meetingId === ownProps.match.params.meetingId);

    if (!groupMeetingResult.length) { return { groupId, groupMembers, viewerIsDi }; }

    return { groupId, meeting: groupMeetingResult[0], groupMembers, viewerIsDi, group };
}

const mapDispatchToProps = (dispatch: any): IDispatchProps => ({
    fetchGroupMeeting: (groupId: string, meetingId: string, cancelToken: CancelTokenSource = axios.CancelToken.source()) => dispatch(fetchGroupMeeting(groupId, meetingId, cancelToken)),
    fetchGroupMembers: (groupId: string, cancelToken: CancelTokenSource = axios.CancelToken.source()) => dispatch(fetchGroupMembers(groupId, cancelToken)),
    fetchGroup: (groupId: string, cancelToken: CancelTokenSource = axios.CancelToken.source()) => dispatch(fetchGroup(groupId, cancelToken)),
    removeMeetingDocument: (groupId: string, meetingId: string, documentId: string, cancelToken: CancelTokenSource = axios.CancelToken.source()) => dispatch(removeMeetingDocument(groupId, meetingId, documentId, cancelToken)),
    signupPerson: (groupId: string, meetingId: string, personId: number, cancelToken: CancelTokenSource = axios.CancelToken.source()) => dispatch(signupPerson(groupId, meetingId, personId, cancelToken)),
    cancelPerson: (groupId: string, meetingId: string, personId: number, cancelToken: CancelTokenSource = axios.CancelToken.source()) => dispatch(cancelPerson(groupId, meetingId, personId, cancelToken))
});

const container = withRouter(connect(mapStateToProps, mapDispatchToProps)(MeetingIndex));

export default container;